import { motion } from "framer-motion";

const steps = [
    {
        n: "01",
        label: "Can I afford a home?",
        title: "See exactly what's within reach.",
        body: "Your rent today is the clearest signal of what you can own tomorrow. We turn it into a home price, a down-payment plan and a confidence range that's honest.",
        score: 62,
    },
    {
        n: "02",
        label: "Where should I buy?",
        title: "Find a neighbourhood that fits.",
        body: "Beyond the listing photos — real pricing, growth patterns and lifestyle fit across the micro-markets aligned to your bracket.",
        score: 71,
    },
    {
        n: "03",
        label: "Is buying worth it?",
        title: "Know if it beats renting.",
        body: "Five and ten years out — the wealth you'll build, the rent you'll save, the right time to exit if you ever want to. The full picture.",
        score: 78,
    },
    {
        n: "04",
        label: "Will the EMI fit my life?",
        title: "Make the loan work for you.",
        body: "Compare lenders, simulate EMIs and find the exact month your mortgage becomes a smaller cheque than your rent.",
        score: 84,
    },
    {
        n: "05",
        label: "Am I making the right choice?",
        title: "A quiet yes before you sign.",
        body: "An optional Vastu read on the home you've shortlisted. Tradition-aware, never preachy — for one last reassurance, on your terms.",
        score: 91,
    },
];

export default function Journey() {
    return (
        <section
            id="how"
            data-testid="homeiq-journey"
            className="relative py-16 md:py-24 px-6 md:px-12 bg-white border-t border-[#0A1F44]/5"
        >
            <div className="max-w-7xl mx-auto">
                <div className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] mb-5 font-semibold">
                            — Your journey home
                        </p>
                        <h2
                            data-testid="journey-heading"
                            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-2xl leading-[1.02] text-[#0A1F44]"
                        >
                            Renting today.
                            <br />
                            <span className="text-[#0A1F44]/45">
                                Owning, sooner than you think.
                            </span>
                        </h2>
                    </div>
                    <p className="text-[#0A1F44]/65 max-w-sm text-base sm:text-lg leading-relaxed">
                        Five questions, answered one by one. Your HomeIQ score
                        grows at every step — until "should I?" quietly becomes
                        "when do I move in?"
                    </p>
                </div>

                <div className="relative">
                    {/* Vertical timeline rail */}
                    <div
                        aria-hidden="true"
                        className="absolute left-[28px] md:left-[56px] top-2 bottom-2 w-px bg-gradient-to-b from-[#0A1F44]/15 via-[#0A1F44]/10 to-transparent hidden sm:block"
                    />

                    <ul className="space-y-3">
                        {steps.map((s, i) => (
                            <motion.li
                                key={s.n}
                                data-testid={`journey-step-${s.n}`}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.7,
                                    delay: i * 0.08,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="group relative border-t border-[#0A1F44]/10 py-6 md:py-9 grid grid-cols-12 gap-4 md:gap-8 items-center hover:border-[#1E6BFF]/60 transition-colors"
                            >
                                <div className="col-span-2 md:col-span-1 relative">
                                    <div className="relative w-10 h-10 md:w-14 md:h-14 rounded-full bg-white border border-[#0A1F44]/15 flex items-center justify-center font-display font-bold text-sm md:text-base text-[#0A1F44] group-hover:bg-[#0A1F44] group-hover:text-white group-hover:border-[#0A1F44] transition-all">
                                        {s.n}
                                    </div>
                                </div>
                                <div className="col-span-10 md:col-span-7">
                                    <div className="text-[11px] tracking-[0.25em] uppercase text-[#1E6BFF] font-semibold mb-2">
                                        {s.label}
                                    </div>
                                    <h3 className="font-display font-bold text-xl sm:text-2xl md:text-3xl tracking-tight text-[#0A1F44] mb-2">
                                        {s.title}
                                    </h3>
                                    <p className="text-[#0A1F44]/65 text-sm sm:text-base leading-relaxed max-w-xl">
                                        {s.body}
                                    </p>
                                </div>
                                <div className="hidden md:flex md:col-span-4 flex-col items-end gap-2">
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#0A1F44]/50 font-semibold">
                                        Your confidence at this stage
                                    </div>
                                    <div className="flex items-center gap-3 w-full max-w-[260px]">
                                        <div className="flex-1 h-1.5 rounded-full bg-[#0A1F44]/10 overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-[#1E6BFF] to-[#5B8DFF] transition-all duration-700"
                                                style={{
                                                    width: `${s.score}%`,
                                                }}
                                            />
                                        </div>
                                        <div className="font-display font-extrabold text-2xl text-[#0A1F44] tabular-nums leading-none">
                                            {s.score}
                                            <span className="text-xs text-[#0A1F44]/40 ml-0.5">
                                                /100
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </motion.li>
                        ))}
                        <li className="border-t border-[#0A1F44]/10" />
                    </ul>
                </div>
            </div>
        </section>
    );
}
